import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { customersAPI } from "../../services/api";
import { CheckCircle, AlertCircle } from "lucide-react";
import PersianDatePicker from "../../utils/PersianDatePicker";
import SearchableSelect from "../../components/SearchableSelect";
import { useEscClose } from "../../hooks/useEscClose";

interface Props { onSuccess: () => void; }
const currencies = ["USD","AFN","IRR","TRY","PKR","AED","EUR"];

export default function AddCustomer({ onSuccess }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState<{ msg: string; ok: boolean } | null>(null);
  const [parents, setParents] = useState<any[]>([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", company_name: "",
    parent_id: "", phone: "", phone2: "",
    address: "", city: "", country: "افغانستان",
    currency: "USD", opening_balance: "", created_at: "",
    notes: "", status: "active",
  });

  useEffect(() => {
    customersAPI.listParents().then(r => { if (r.success) setParents(r.customers); });
  }, []);

  const showToast = (msg: string, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3500); };
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  useEscClose(onSuccess);

  const balVal   = parseFloat(form.opening_balance || "0");
  const balType  = balVal >= 0 ? "debit" : "credit";
  const balColor = balVal > 0 ? "#ef4444" : balVal < 0 ? "#10b981" : "#64748b";
  const balDesc  = balVal > 0
    ? `🔴 مشتری ${new Intl.NumberFormat("en-US").format(Math.abs(balVal))} ${form.currency} به ما قرضدار است`
    : balVal < 0
    ? `🟢 ما ${new Intl.NumberFormat("en-US").format(Math.abs(balVal))} ${form.currency} به مشتری قرضداریم`
    : "✅ بیلانس صفر — حساب تسویه";

  const inp: React.CSSProperties = {
    width: "100%", padding: "10px 14px",
    background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 10, color: T.text, fontSize: 13,
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    direction: "rtl" as const, outline: "none", boxSizing: "border-box" as const,
  };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 };
  const card: React.CSSProperties = { borderRadius: 14, padding: 20, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}`, marginBottom: 16 };

  const parentOptions  = parents.map(p => ({ value: String(p.id), label: `${p.first_name} ${p.last_name}${p.company_name ? ` (${p.company_name})` : ""} — ${p.code}` }));
  const currencyOptions = currencies.map(c => ({ value: c, label: c }));
  const statusOptions   = [{ value: "active", label: "فعال" }, { value: "inactive", label: "غیرفعال" }];

  const handleSubmit = async () => {
    if (!form.first_name.trim()) { showToast("نام مشتری الزامی است", false); return; }
    setSaving(true);
    try {
      const r = await customersAPI.add({
        ...form,
        parent_id: form.parent_id ? parseInt(form.parent_id) : null,
        opening_balance: Math.abs(balVal),
        opening_balance_type: balType,
      });
      if (r.success) { showToast(`✅ مشتری ${r.code} ثبت شد`); setTimeout(onSuccess, 1200); }
      else showToast(r.message, false);
    } catch { showToast("خطا در اتصال به سرور", false); }
    setSaving(false);
  };

  return (
    <div dir="rtl" style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999, padding: "12px 24px", borderRadius: 12, background: dark ? "#1e293b" : "#fff", border: `2px solid ${toast.ok ? "#10b981" : "#ef4444"}`, boxShadow: "0 8px 32px rgba(0,0,0,0.25)", color: toast.ok ? "#10b981" : "#ef4444", fontSize: 13, fontWeight: 700, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8 }}>
          {toast.ok ? <CheckCircle size={15} /> : <AlertCircle size={15} />} {toast.msg}
        </div>
      )}

      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 18 }}>👤 اطلاعات مشتری</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <div><label style={lbl}>نام *</label><input value={form.first_name} onChange={e => set("first_name", e.target.value)} placeholder="نام مشتری" style={inp} /></div>
          <div><label style={lbl}>تخلص</label><input value={form.last_name} onChange={e => set("last_name", e.target.value)} placeholder="تخلص" style={inp} /></div>
          <div><label style={lbl}>نام شرکت / تجارت</label><input value={form.company_name} onChange={e => set("company_name", e.target.value)} placeholder="اختیاری" style={inp} /></div>
          <div><label style={lbl}>شماره تماس</label><input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+93..." style={{ ...inp, direction: "ltr" as const }} /></div>
          <div><label style={lbl}>شماره تماس ۲</label><input value={form.phone2} onChange={e => set("phone2", e.target.value)} placeholder="+93..." style={{ ...inp, direction: "ltr" as const }} /></div>
          <SearchableSelect label="وضعیت" options={statusOptions} value={form.status} onChange={v => set("status", v || "active")} placeholder="— انتخاب"/>
          <div><label style={lbl}>شهر / ولایت</label><input value={form.city} onChange={e => set("city", e.target.value)} placeholder="کابل" style={inp} /></div>
          <div><label style={lbl}>کشور</label><input value={form.country} onChange={e => set("country", e.target.value)} style={inp} /></div>
          <SearchableSelect label="زیرمجموعه‌ی" options={parentOptions} value={form.parent_id} onChange={v => set("parent_id", v)} placeholder="— مشتری اصلی (بدون والد)"/>
          <div><PersianDatePicker label="تاریخ ثبت *" value={form.created_at} onChange={v => set("created_at", v)} /></div>
          <div style={{ gridColumn: "1/-1" }}><label style={lbl}>آدرس</label><input value={form.address} onChange={e => set("address", e.target.value)} placeholder="آدرس کامل..." style={inp} /></div>
        </div>
      </div>

      <div style={{ ...card, borderRight: "4px solid #8b5cf6" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 6 }}>💰 بیلانس اولیه</div>
        <div style={{ fontSize: 12, color: T.subtext, marginBottom: 16 }}>
          عدد <strong style={{ color: "#ef4444" }}>مثبت</strong> = مشتری به ما قرضدار است &nbsp;|&nbsp;
          عدد <strong style={{ color: "#10b981" }}>منفی</strong> = ما به مشتری قرضداریم
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <label style={lbl}>بیلانس اولیه (مثبت یا منفی)</label>
            <input type="number" value={form.opening_balance} onChange={e => set("opening_balance", e.target.value)} placeholder="مثال: 5000 یا -3000" style={{ ...inp, fontSize: 15, fontWeight: 700, color: balColor }} />
          </div>
          <SearchableSelect label="ارز مشتری" options={currencyOptions} value={form.currency} onChange={v => set("currency", v || "USD")} placeholder="— ارز"/>
        </div>
        {form.opening_balance !== "" && (
          <div style={{ marginTop: 12, padding: "10px 16px", borderRadius: 10, background: balVal > 0 ? "rgba(239,68,68,0.06)" : balVal < 0 ? "rgba(16,185,129,0.06)" : "rgba(148,163,184,0.06)", border: `1px solid ${balColor}30`, fontSize: 13, fontWeight: 700, color: balColor }}>
            {balDesc}
          </div>
        )}
      </div>

      <div style={card}>
        <label style={lbl}>یادداشت</label>
        <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={3} placeholder="هر توضیحی درباره مشتری..." style={{ ...inp, resize: "vertical" as const }} />
      </div>

      <button onClick={handleSubmit} disabled={saving} style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "white", fontSize: 15, fontWeight: 800, fontFamily: "inherit", cursor: "pointer" }}>
        {saving ? "در حال ثبت..." : "✅ ثبت مشتری"}
      </button>
    </div>
  );
}